import {
  Layout,
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Typography,
  message,
  Table,
  Spin,
  Progress,
} from 'antd';

import {
  MailOutlined,
  MobileOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  UserOutlined,
  LockOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

import { useState, useEffect } from 'react';

import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { decryptData } from '../middleware/encryption';

import logo from '../assets/loader.jpeg';
import usage_illu from '../assets/pricing-desk.png';

const { Content } = Layout;
const { Title, Text } = Typography;

const customIcon = (
  <img
    src={logo}
    alt="Loading"
    width={60}
    height={60}
    style={{
      animation: 'spin 1s linear infinite',
      borderRadius: '50%',
      transformOrigin: 'center',
      padding: 5,
    }}
  />
);

Spin.setDefaultIndicator(customIcon);

const historyColumns = [
  {
    title: 'Export Date',
    dataIndex: 'export_date',
    key: 'export_date',
    render: (text) =>
      new Date(text).toLocaleString(),
  },
  {
    title: 'File Name',
    dataIndex: 'file_name',
    key: 'file_name',
  },
  {
    title: 'Exported Rows',
    dataIndex: 'export_row_count',
    key: 'export_row_count',
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    render: (text) =>
      text.charAt(0).toUpperCase() +
      text.slice(1),
  },
  {
    title: 'Download',
    dataIndex: 'file_url',
    key: 'file_url',
    render: (url) => (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Download
      </a>
    ),
  },
];

export default function Settings() {
  const [collapsed, setCollapsed] =
    useState(false);

  const [form] = Form.useForm();

  const [userDetails, setUserDetails] =
    useState({});

  const [usageData, setUsageData] =
    useState(null);

  const [historyData, setHistoryData] =
    useState([]);

  const [planType, setPlanType] =
    useState('Starter');

  const [activeTab, setActiveTab] =
    useState('1');

  const [loading, setLoading] =
    useState(true);

  const [historyPagination, setHistoryPagination] =
    useState({
      current: 1,
      pageSize: 10,
      total: 0,
    });

  const [historyLoading, setHistoryLoading] =
    useState(false);

  useEffect(() => {
    const fetchUserUsage = async () => {
      setLoading(true);

      try {
        const myHeaders = new Headers();

        const token =
          sessionStorage.getItem(
            'access_token'
          );

        if (token) {
          myHeaders.append(
            'Authorization',
            `Bearer ${token}`
          );
        }

        const requestOptions = {
          method: 'GET',
          headers: myHeaders,
          redirect: 'follow',
        };

        const userResponse = await fetch(
          'https://api.app.exellius.com/api/users/getUserPoints',
          requestOptions
        );

        const userResult =
          await userResponse.json();

        if (userResult.data) {
          setUsageData(
            decryptData(userResult.data)
              .points
          );
        } else {
          message.error(
            userResult.message ||
              'Failed to fetch user details'
          );
        }
      } catch (error) {
        console.error(error);

        message.error(
          'An error occurred while fetching user data'
        );
      }

      setLoading(false);
    };

    fetchUserUsage();
  }, []);

  const fetchExportHistory = async (
    page = 1,
    limit = 10
  ) => {
    setHistoryLoading(true);

    try {
      const myHeaders = new Headers();

      const token =
        sessionStorage.getItem(
          'access_token'
        );

      if (token) {
        myHeaders.append(
          'Authorization',
          `Bearer ${token}`
        );
      }

      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow',
      };

      const response = await fetch(
        `https://api.app.exellius.com/api/leads/getExportedFiles?type=leads&page=${page}&limit=${limit}`,
        requestOptions
      );

      const result =
        await response.json();

      if (result.data) {
        const decryptedData = decryptData(
          result.data
        );

        setHistoryData(
          decryptedData.data
        );

        setHistoryPagination(
          (prev) => ({
            ...prev,
            current:
              decryptedData.pagination
                .page,
            pageSize: limit,
            total:
              decryptedData.pagination
                .total || 0,
          })
        );
      } else {
        message.error(
          'Failed to fetch export history'
        );
      }
    } catch (error) {
      console.error(error);

      message.error(
        'An error occurred while fetching export history'
      );
    }

    setHistoryLoading(false);
  };

  const formatCredits = (credits) => {
    return credits || 0;
  };

  const handleUpdatePassword = async (
    values
  ) => {
    try {
      const myHeaders = new Headers();

      myHeaders.append(
        'Content-Type',
        'application/json'
      );

      const token =
        sessionStorage.getItem(
          'access_token'
        );

      if (token) {
        myHeaders.append(
          'Authorization',
          `Bearer ${token}`
        );
      }

      const raw = JSON.stringify({
        newPassword:
          values.newPassword,
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
        redirect: 'follow',
      };

      await fetch(
        'https://api.app.exellius.com/api/users/updatePassword',
        requestOptions
      );

      message.success(
        'Password updated successfully'
      );
    } catch (error) {
      console.error(error);

      message.error(
        'Failed to update password'
      );
    }
  };

  const tabs = [
    {
      key: '1',
      label: 'Usage',
      icon: <BarChartOutlined />,
    },
    {
      key: '2',
      label: 'Account Details',
      icon: <UserOutlined />,
    },
    {
      key: '3',
      label: 'Update Password',
      icon: <LockOutlined />,
    },
    {
      key: '4',
      label: 'Export History',
      icon: <DownloadOutlined />,
    },
  ];

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: '#f7f8fc',
      }}
    >
      <Sidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
      />

      <Layout>
        <AppHeader />

        <Content
          style={{
            padding: 24,
            overflowY: 'auto',
            height:
              'calc(100vh - 64px)',
          }}
        >
          {/* HEADER */}
          <div
            style={{
              marginBottom: 24,
            }}
          >
            <Title
              level={2}
              style={{
                margin: 0,
                fontWeight: 700,
              }}
            >
              Settings
            </Title>

            <Text
              style={{
                color: '#667085',
              }}
            >
              Manage your account,
              preferences and data.
            </Text>
          </div>

          {/* TABS */}
          <Card
            bordered={false}
            style={{
              borderRadius: 18,
              marginBottom: 24,
            }}
            bodyStyle={{
              padding: 10,
            }}
          >
            <Row gutter={[10, 10]}>
              {tabs.map((tab) => (
                <Col key={tab.key}>
                  <Button
                    icon={tab.icon}
                    type={
                      activeTab === tab.key
                        ? 'primary'
                        : 'text'
                    }
                    onClick={() => {
                      setActiveTab(
                        tab.key
                      );

                      if (
                        tab.key === '4'
                      ) {
                        fetchExportHistory(
                          1,
                          historyPagination.pageSize
                        );
                      }
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      paddingInline: 18,
                      fontWeight: 600,
                      background:
                        activeTab ===
                        tab.key
                          ? '#6d4aff'
                          : 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                    }}
                  >
                    {tab.label}
                  </Button>
                </Col>
              ))}
            </Row>
          </Card>

          {/* TAB CONTENT */}
          {activeTab === '1' && (
            <div
              style={{
                background: '#fff',
                borderRadius: 24,
                border:
                  '1px solid #f1f1f4',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding:
                    '30px 32px',
                  borderBottom:
                    '1px solid #f5f5f7',
                }}
              >
                <Title
                  level={3}
                  style={{
                    margin: 0,
                  }}
                >
                  Usage details
                </Title>
              </div>

              {loading ? (
                <div
                  style={{
                    padding: 80,
                    textAlign:
                      'center',
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  {/* PLAN */}
                  <div
                    style={{
                      padding:
                        '0px 50px',
                      display: 'flex',
                      justifyContent:
                        'space-between',
                      alignItems:
                        'center',
                      flexWrap:
                        'wrap',
                    }}
                  >
                    <div>
                      <Text
                        style={{
                          color:
                            '#667085',
                          display:
                            'block',
                          marginBottom: 8,
                        }}
                      >
                        Your Plan
                      </Text>

                      <Title
                        level={2}
                        style={{
                          margin: 0,
                          color:
                            '#6d4aff',
                        }}
                      >
                        {planType}
                      </Title>

                      <div
                        style={{
                          marginTop: 14,
                          background:
                            '#f3f0ff',
                          width:
                            'fit-content',
                          padding:
                            '6px 14px',
                          borderRadius: 50,
                          color:
                            '#6d4aff',
                          fontWeight: 600,
                        }}
                      >
                        Credits:{' '}
                        {formatCredits(
                          usageData
                        )}
                      </div>
                    </div>

                    <img
                      src={usage_illu}
                      alt="usage"
                      style={{
                        height: 300,
                      }}
                    />
                  </div>

                  {/* CARDS */}
                  <Row
                    gutter={[22, 22]}
                    style={{
                      padding:
                        '0 32px 32px',
                    }}
                  >
                    {[
                      {
                        title:
                          'Email Credits',
                        value:
                          formatCredits(
                            usageData
                          ),
                        icon: (
                          <MailOutlined />
                        ),
                        percent: 52,
                      },
                      {
                        title:
                          'Email Verification Credits',
                        value:
                          formatCredits(
                            usageData
                          ),
                        icon: (
                          <CheckCircleOutlined />
                        ),
                        percent: 52,
                      },
                      {
                        title:
                          'Mobile Phone Credits',
                        value:
                          formatCredits(
                            Math.floor(
                              usageData /
                                5
                            )
                          ),
                        icon: (
                          <MobileOutlined />
                        ),
                        percent: 10,
                      },
                    ].map(
                      (
                        item,
                        index
                      ) => (
                        <Col
                          xs={24}
                          md={12}
                          lg={8}
                          key={index}
                        >
                          <div
                            style={{
                              border:
                                '1px solid #f2f2f5',
                              borderRadius: 20,
                              padding: 28,
                              background:
                                '#fff',
                            }}
                          >
                            <div
                              style={{
                                width: 62,
                                height: 62,
                                borderRadius:
                                  '50%',
                                background:
                                  '#f3efff',
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                justifyContent:
                                  'center',
                                color:
                                  '#6d4aff',
                                fontSize: 24,
                                margin:
                                  '0 auto 18px',
                              }}
                            >
                              {
                                item.icon
                              }
                            </div>

                            <div
                              style={{
                                textAlign:
                                  'center',
                              }}
                            >
                              <Text>
                                {
                                  item.title
                                }
                              </Text>

                              <Title
                                level={
                                  1
                                }
                                style={{
                                  margin:
                                    '10px 0 0',
                                  color:
                                    '#6d4aff',
                                  fontSize: 42,
                                }}
                              >
                                {
                                  item.value
                                }
                              </Title>

                              <Text
                                type="secondary"
                              >
                                credits
                                available
                              </Text>
                            </div>

                            <div
                              style={{
                                marginTop: 22,
                              }}
                            >
                              <Progress
                                percent={
                                  item.percent
                                }
                                showInfo={
                                  false
                                }
                                strokeColor="#6d4aff"
                                trailColor="#ebe9fe"
                                strokeWidth={
                                  8
                                }
                              />

                              <div
                                style={{
                                  display:
                                    'flex',
                                  justifyContent:
                                    'space-between',
                                  marginTop: 8,
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 12,
                                  }}
                                >
                                  0
                                </Text>

                                <Text
                                  style={{
                                    fontSize: 12,
                                  }}
                                >
                                  1,000
                                </Text>
                              </div>
                            </div>
                          </div>
                        </Col>
                      )
                    )}
                  </Row>
                </>
              )}
            </div>
          )}

          {activeTab === '2' && (
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
              }}
            >
              <div style={{ width:'50%'}}>
              <Title level={3}>
                Account Details
              </Title>

              <Form
                form={form}
                layout="vertical"
                initialValues={
                  userDetails
                }
              >
                <Row gutter={20}>
                  <Col
                    xs={24}
                    md={12}
                  >
                    <Form.Item
                      name="firstName"
                      label="First Name"
                    >
                      <Input
                        size="large"
                        style={{
                          borderRadius: 12,
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col
                    xs={24}
                    md={12}
                  >
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                    >
                      <Input
                        size="large"
                        style={{
                          borderRadius: 12,
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="email"
                  label="Email"
                >
                  <Input
                    size="large"
                    style={{
                      borderRadius: 12,
                    }}
                  />
                </Form.Item>

                <Button
                  type="primary"
                  size="large"
                  style={{
                    height: 48,
                    borderRadius: 12,
                    background:
                      '#6d4aff',
                    border: 'none',
                    paddingInline: 28,
                  }}
                >
                  Update Details
                </Button>
              </Form>
              </div>
            </Card>
          )}

          {activeTab === '3' && (
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
              }}
            >

              <div style={{width:'50%', marginBottom: 24}}>
              <Title level={3}>
                Update Password
              </Title>

              <Form
                form={form}
                layout="vertical"
                onFinish={
                  handleUpdatePassword
                }
              >
                <Form.Item
                  name="newPassword"
                  label="New Password"
                  rules={[
                    {
                      required: true,
                      message:
                        'Please enter your new password',
                    },
                  ]}
                >
                  <Input.Password
                    size="large"
                    style={{
                      borderRadius: 12,
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Confirm Password"
                  dependencies={[
                    'newPassword',
                  ]}
                  rules={[
                    {
                      required: true,
                      message:
                        'Please confirm your password',
                    },
                    ({
                      getFieldValue,
                    }) => ({
                      validator(
                        _,
                        value
                      ) {
                        return !value ||
                          getFieldValue(
                            'newPassword'
                          ) === value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                'Passwords do not match'
                              )
                            );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    style={{
                      borderRadius: 12,
                    }}
                  />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  style={{
                    height: 48,
                    borderRadius: 12,
                    background:
                      '#6d4aff',
                    border: 'none',
                    paddingInline: 28,
                  }}
                >
                  Update Password
                </Button>
              </Form>
              </div>
            </Card>
          )}

          {activeTab === '4' && (
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
              }}
            >
              <Title level={3}>
                Export History
              </Title>

              <Table
                dataSource={
                  historyData
                }
                columns={
                  historyColumns
                }
                rowKey="id"
                loading={
                  historyLoading
                }
                pagination={{
                  current:
                    historyPagination.current,
                  pageSize:
                    historyPagination.pageSize,
                  total:
                    historyPagination.total,
                  onChange: (
                    page,
                    pageSize
                  ) => {
                    fetchExportHistory(
                      page,
                      pageSize
                    );
                  },
                }}
              />
            </Card>
          )}
        </Content>
      </Layout>
    </Layout>
  );
}